import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { Settings } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Container, ContainerContent } from '@/components/ui/Container'
import { Pagination } from '@/components/ui/Pagination'
import { ListRefreshIndicator } from '@/features/order-confirmation/components/ListRefreshIndicator'
import { getSalesOrderCustomers } from '@/services/api'
import { salesOrderQueryOptions } from '@/features/sales-order/salesOrderQueries'
import type { CustomerListItem } from '@/types/salesOrder'

function EmailDomains({ domains }: { domains: string[] }) {
  if (domains.length === 0) {
    return <span className="text-sm italic text-gray-400">No email domains</span>
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {domains.map((domain) => (
        <span
          key={domain}
          className="rounded-md border border-gray-200 bg-white px-2 py-0.5 text-[11px] text-gray-600"
        >
          {domain}
        </span>
      ))}
    </div>
  )
}

/** The same indicator the supplier list carries. */
function SettingsStatus({ hasSettings }: { hasSettings: boolean }) {
  return (
    <span className={`text-xs ${hasSettings ? 'text-gray-600' : 'italic text-gray-400'}`}>
      {hasSettings ? 'Custom settings' : 'Uses global settings'}
    </span>
  )
}

function CustomerRow({
  customer,
  onOpenSettings,
}: {
  customer: CustomerListItem
  onOpenSettings: (customer: CustomerListItem) => void
}) {
  return (
    <tr className="border-t border-gray-100 transition-colors hover:bg-gray-50">
      <td className="px-4 py-3">
        <p className="font-medium text-gray-900">{customer.name}</p>
        <p className="text-xs text-gray-400">{customer.number || 'No number'}</p>
      </td>
      <td className="hidden md:table-cell px-4 py-3">
        <EmailDomains domains={customer.email_domains} />
      </td>
      <td className="hidden sm:table-cell px-4 py-3">
        <SettingsStatus hasSettings={customer.has_settings} />
      </td>
      <td className="px-4 py-3 text-right">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-gray-500 hover:text-gray-900"
          aria-label={`Settings for ${customer.name}`}
          onClick={() => onOpenSettings(customer)}
        >
          <Settings className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  )
}

export function CustomersPage() {
  const { workflowId } = useParams<{ workflowId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [createTarget, setCreateTarget] = useState<CustomerListItem | null>(null)
  const page = Number(searchParams.get('page')) || 1

  const { data, isLoading, isFetching, isError, error } = useQuery({
    queryKey: ['sales-order-customers', workflowId, page],
    queryFn: () => getSalesOrderCustomers(workflowId!, page),
    enabled: !!workflowId,
    // The table stays on screen while the next page loads, rather than collapsing to the loading
    // state and back, which is what makes paging through a long list bearable.
    placeholderData: keepPreviousData,
    ...salesOrderQueryOptions,
  })

  const isBackgroundRefreshing = isFetching && !isLoading && !!data
  const customers = data?.data ?? []

  function settingsPath(customer: CustomerListItem, create: boolean) {
    const name = encodeURIComponent(customer.name)
    const createParam = create ? 'create=1&' : ''
    return `/sales-order/${workflowId}/customers/${customer.id}/settings?${createParam}name=${name}`
  }

  // A customer with no settings of their own is asked about first: an override starts as a copy of
  // the workflow's settings, and from then on stops following them.
  function openSettings(customer: CustomerListItem) {
    if (customer.has_settings) {
      navigate(settingsPath(customer, false))
      return
    }
    setCreateTarget(customer)
  }

  function confirmCreateSettings() {
    if (!createTarget || !workflowId) return
    navigate(settingsPath(createTarget, true))
    setCreateTarget(null)
  }

  // The page lives in the URL, so a particular page can be linked to and survives a reload.
  function goToPage(next: number) {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current)
        if (next > 1) params.set('page', String(next))
        else params.delete('page')
        return params
      },
      { replace: true },
    )
  }

  if (!workflowId) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
        No workflow selected.
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
    )
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-red-600">
        {error instanceof Error ? error.message : 'Could not load customers'}
      </div>
    )
  }

  return (
    <div className="container mx-auto">
      <Container>
        <ContainerContent className="py-6">
          <header className="mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Customers</h1>
          </header>

          <div className="relative">
            <ListRefreshIndicator visible={isBackgroundRefreshing} />
            <div className="data-sheet">
            <table className="w-full text-left">
              <thead>
                <tr className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="px-4 pb-2">Customer</th>
                  <th className="hidden md:table-cell px-4 pb-2">Email domains</th>
                  <th className="hidden sm:table-cell px-4 pb-2">Settings</th>
                  <th className="px-4 pb-2 text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {customers.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-16 text-center text-sm text-gray-400">
                      No customers found
                    </td>
                  </tr>
                ) : (
                  customers.map((customer) => (
                    <CustomerRow
                      key={customer.id}
                      customer={customer}
                      onOpenSettings={openSettings}
                    />
                  ))
                )}
              </tbody>
            </table>
            </div>
          </div>

          {data && (
            <Pagination
              page={data.page}
              totalCount={data.total}
              pageSize={data.page_size}
              onPage={goToPage}
            />
          )}
        </ContainerContent>
      </Container>

      <Dialog.Root
        open={createTarget != null}
        onOpenChange={(open) => !open && setCreateTarget(null)}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-panel bg-white p-6 shadow-lg">
            <Dialog.Title className="text-base font-semibold text-gray-900">
              Create customer settings
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-500">
              {createTarget
                ? `Create custom settings for ${createTarget.name}? These will override the global settings for this customer.`
                : null}
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateTarget(null)}>
                Cancel
              </Button>
              <Button type="button" onClick={confirmCreateSettings}>
                Create settings
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
